/**
 * Планирование — главный экран модуля.
 *
 * Расчёт: для всех активных production_objects с привязанным BOM (kit_id)
 * раскрываем нормативы, суммируем по item_id, сравниваем с остатками на
 * складе минус резерв, считаем «нужно дозаказать».
 *
 * Ради этой страницы был сделан весь Phase 3.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Spinner } from "@/components/Spinner";
import {
  fetchPlanning, fetchKits, setObjectKit, formatRub,
  type PlanningProject, type KitRow,
} from "@/api/warehouse";
import { ApiError } from "@/api/client";


export function WarehousePlanningPage() {
  const qc = useQueryClient();
  const [onlyDeficit, setOnlyDeficit] = useState(true);

  const planQ = useQuery({
    queryKey: ["warehouse-planning"],
    queryFn: () => fetchPlanning(),
  });
  const kitsQ = useQuery({
    queryKey: ["warehouse-kits"],
    queryFn: () => fetchKits(),
  });
  const kits = kitsQ.data || [];

  const setKitMut = useMutation({
    mutationFn: (vars: { objectId: number; kitId: number | null }) =>
      setObjectKit(vars.objectId, vars.kitId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-planning"] });
    },
  });

  if (planQ.isLoading || kitsQ.isLoading) return <Spinner size="lg" label="Считаем планирование…" />;
  if (planQ.error) {
    const err = planQ.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }

  const data = planQ.data!;
  const rows = onlyDeficit
    ? data.required.filter(r => r.need_to_order > 0)
    : data.required;
  const totalProjects = data.projects.with_kit.length + data.projects.without_kit.length;
  const summary = data.summary;

  return (
    <div className="page">

      {totalProjects === 0 ? (
        <EmptyNoProjects />
      ) : (
        <>
          {/* KPI блок */}
          <div className="row row-4" style={{ marginBottom: "var(--gap)" }}>
            <KPI label="Активных проектов" value={`${totalProjects}`} foot={
              <>{data.projects.with_kit.length} с BOM · {data.projects.without_kit.length} без</>
            } />
            <KPI label="Уникальных позиций" value={`${summary.items}`} foot="в нормативах" />
            <KPI label="Дефицит" value={`${summary.need_to_order_items}`}
                 alarm={summary.need_to_order_items > 0}
                 foot={summary.need_to_order_items > 0 ? "позиций нужно дозаказать" : "запасов хватает"} />
            <KPI label="Бюджет дозаказа" value={summary.total_need_cost ? formatRub(summary.total_need_cost) : "—"}
                 foot="по закупочным ценам" />
          </div>

          {/* Проекты без привязки BOM — критично */}
          {data.projects.without_kit.length > 0 && (
            <div className="card" style={{ padding: 16, marginBottom: "var(--gap)", borderLeft: "3px solid var(--brick)" }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                {data.projects.without_kit.length} проект(ов) без привязанного типа беседки —
                их материалы НЕ учтены в расчёте
              </div>
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Клиент</th>
                      <th>Регион</th>
                      <th>м²</th>
                      <th>Срок</th>
                      <th style={{ width: 280 }}>Привязать тип беседки →</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.projects.without_kit.map(p => (
                      <ProjectRow key={p.id} project={p} kits={kits} onSetKit={(kid) => setKitMut.mutate({ objectId: p.id, kitId: kid })} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Главная таблица: required */}
          {rows.length === 0 && data.required.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--mute)" }}>
              Нормативы расхода пустые — добавьте позиции в наборы на странице{" "}
              <Link to={"/kits" as "/"} className="card-link">Наборы</Link>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--line-soft)" }}>
                <div style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700 }}>
                  Что нужно дозаказать
                </div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={onlyDeficit} onChange={(e) => setOnlyDeficit(e.target.checked)} />
                  Только дефицитные ({data.required.filter(r => r.need_to_order > 0).length} из {data.required.length})
                </label>
              </div>
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>Артикул</th>
                      <th>Наименование</th>
                      <th>Раздел</th>
                      <th className="num" title="Нужно по всем проектам с привязанным BOM">Нужно</th>
                      <th className="num" title="Свободный остаток = на складе − бронь">Доступно</th>
                      <th className="num" title="Уже заказано у поставщика (открытые PO)">В пути</th>
                      <th className="num" title="Дефицит = Нужно − Доступно − В пути">Дозаказать</th>
                      <th className="num">Цена закуп</th>
                      <th className="num">Сумма дозаказа</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={9} style={{ textAlign: "center", padding: 30, color: "var(--mute)" }}>
                        Запасов хватает на все активные проекты
                      </td></tr>
                    ) : rows.map(r => {
                      const def = r.need_to_order > 0;
                      return (
                        <tr key={r.item_id} style={def ? { background: "rgba(171,43,43,0.04)" } : undefined}>
                          <td className="tabular">{r.sku || "—"}</td>
                          <td>{r.name}</td>
                          <td style={{ fontSize: 12, color: "var(--mute)" }}>{r.section || "—"}</td>
                          <td className="num tabular strong">{r.required} {r.unit || ""}</td>
                          <td className="num tabular" style={{ color: r.available < 0 ? "var(--brick)" : undefined }}>
                            {r.available} {r.unit || ""}
                          </td>
                          <td className="num tabular" style={{ color: r.on_order > 0 ? "#3390ec" : "var(--mute)" }}>
                            {r.on_order > 0 ? `${r.on_order} ${r.unit || ""}` : "—"}
                          </td>
                          <td className="num tabular strong" style={{ color: def ? "var(--brick)" : "var(--mute)" }}>
                            {def ? r.need_to_order : "—"}
                          </td>
                          <td className="num tabular" style={{ color: "var(--mute)" }}>
                            {r.price_buy > 0 ? formatRub(r.price_buy) : "—"}
                          </td>
                          <td className="num tabular strong">
                            {r.estimated_cost != null && def ? formatRub(r.estimated_cost) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {rows.length > 0 && summary.total_need_cost && (
                    <tfoot>
                      <tr>
                        <td colSpan={8} style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700 }}>Итого дозаказать:</td>
                        <td className="num tabular strong" style={{ color: "var(--brick)" }}>
                          {formatRub(summary.total_need_cost)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* Проекты с привязанным BOM — справочно */}
          {data.projects.with_kit.length > 0 && (
            <details style={{ marginTop: "var(--gap)" }}>
              <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 13, padding: 8 }}>
                {data.projects.with_kit.length} проект(ов) с привязанным типом беседки (учтены в расчёте)
              </summary>
              <div className="card" style={{ padding: 0, marginTop: 8 }}>
                <div className="tbl-scroll">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Клиент</th>
                        <th>Регион</th>
                        <th>м²</th>
                        <th>Срок</th>
                        <th>Тип беседки (BOM)</th>
                        <th style={{ width: 200 }}/>
                      </tr>
                    </thead>
                    <tbody>
                      {data.projects.with_kit.map(p => (
                        <ProjectRow key={p.id} project={p} kits={kits}
                                    onSetKit={(kid) => setKitMut.mutate({ objectId: p.id, kitId: kid })} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}


function KPI({ label, value, foot, alarm }: { label: string; value: string; foot?: React.ReactNode; alarm?: boolean }) {
  return (
    <div className={`kpi ${alarm ? "alarm" : ""}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value kpi-value-sm">{value}</div>
      {foot && <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>{foot}</span></div>}
    </div>
  );
}


function ProjectRow({ project, kits, onSetKit }: {
  project: PlanningProject;
  kits: KitRow[];
  onSetKit: (kitId: number | null) => void;
}) {
  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{project.client_name || `#${project.id}`}</div>
        <div style={{ fontSize: 11, color: "var(--mute)" }}>
          ID {project.id} {project.deal_amo_id ? `· Amo ${project.deal_amo_id}` : ""}
        </div>
      </td>
      <td>{project.region || "—"}</td>
      <td className="num tabular">{project.sqm ?? "—"}</td>
      <td className="tabular" style={{ fontSize: 12 }}>
        {project.planned_end || "—"}
      </td>
      <td>
        {project.kit_name ? (
          <span className="strong">{project.kit_name}</span>
        ) : (
          <span className="muted">— не привязан —</span>
        )}
      </td>
      <td>
        <select
          className="input-sm"
          value={project.kit_id ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            onSetKit(v === "" ? null : parseInt(v, 10));
          }}
        >
          <option value="">— Без BOM —</option>
          {kits.map(k => (
            <option key={k.id} value={k.id}>
              {k.name}{k.sqm ? ` · ${k.sqm} м²` : ""}
            </option>
          ))}
        </select>
      </td>
    </tr>
  );
}


function EmptyNoProjects() {
  return (
    <div className="card" style={{ textAlign: "center", padding: 40, color: "var(--mute)" }}>
      <div style={{ fontSize: 18, fontFamily: "var(--serif)", marginBottom: 8 }}>
        Активных проектов нет
      </div>
      <div>Когда в Production появятся объекты в статусе in_progress/planned — здесь будет расчёт нужных материалов.</div>
    </div>
  );
}
